import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useMetricsData } from '../../lib/admin-hooks';
import { useMetricsDateRange, generateMetricsCsv, downloadCsv } from '../../lib/metrics-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency } from '../../lib/utils';
import { MetricsDateFilter } from '../../components/admin/MetricsDateFilter';
import { MetricsPeakInsights } from '../../components/admin/MetricsPeakInsights';
import { DailySalesChart, HourlySalesChart, ChannelPieChart, TopProductsChart } from '../../components/admin/MetricsCharts';
import { DownloadIcon, TrendingUp, TrendingDown, Loader2Icon, ChevronDown } from 'lucide-react';
import { OrdersHistoryTable } from '../../components/admin/OrdersHistoryTable';
import { CabanaCashClose } from '../../components/admin/CabanaCashClose';
import { DeliveryMsExpressSection } from '../../components/admin/DeliveryMsExpressSection';
function getDefaultDateRange(): {
  start: string;
  end: string;
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return {
    start: fmt(firstDay),
    end: fmt(today)
  };
}
export function MetricsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    profitability,
    loading: metricsLoading
  } = useMetricsData(restaurant?.id);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const {
    orders,
    orderItems,
    analytics,
    loading: analyticsLoading
  } = useMetricsDateRange(restaurant?.id, startDate, endDate);
  const [isExporting, setIsExporting] = useState(false);
  // Profitability section open/closed. Defaults to OPEN so existing users
  // see the same view as before; the chevron in the header collapses it
  // when the table gets long. State is local and ephemeral by design —
  // no localStorage so each page visit starts in a predictable place.
  const [isProfitabilityOpen, setIsProfitabilityOpen] = useState(true);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-5381" data-mp="1upnmhncedpzc">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-5382" data-mp="1pq183aku23oy" />
      </div>;
  }
  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };
  const handleExportCsv = () => {
    setIsExporting(true);
    try {
      const csv = generateMetricsCsv(analytics, orders);
      const filename = `metricas_${restaurant.slug}_${startDate}_${endDate}.csv`;
      downloadCsv(csv, filename);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    } finally {
      setTimeout(() => setIsExporting(false), 600);
    }
  };
  // ─── Tenant-scoped feature: Cierre de Caja ─────────────────────────
  // Restaurantes que cobran en barra/mesa con flujo propio se benefician
  // del Cierre de Caja: La Cabaña Parrillera y NatuCafé Coffee Shop.
  // Se monta el MISMO componente para ambos (es restaurant-agnostic);
  // solo cambia el restaurantId que recibe.
  //
  // 🔒 MULTI-TENANT SAFETY: el componente recibe `restaurant.id` del
  // restaurante CARGADO desde la URL — nunca se hardcodea un id. La
  // detección es solo para decidir SI mostrar el bloque o no; los datos
  // que se muestran salen siempre del id real del tenant.
  const isCabanaParrillera = String(restaurant?.id) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  // Detección permisiva de NatuCafé: por id (19), por slug exacto, o por
  // cualquier slug que contenga "natucafe" (cubre variantes como
  // "natucafe", "natucafe-coffee-shop", "natucafe-cr", etc.).
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop' || (restaurant?.slug?.toLowerCase().includes('natucafe') ?? false);
  // Emilius Pizza (id 27) — usa el mismo flujo de KDS / Cierre de Caja
  // que NatuCafé. Mismo componente, mismo comportamiento; solo cambia el
  // restaurantId que recibe. Emilius NO toca a NatuCafé.
  const isEmilius = String(restaurant?.id) === '27' || restaurant?.slug === 'emilius-pizza';
  const showCashClose = isCabanaParrillera || isNatuCafe || isEmilius;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5383" data-mp="1xr09ai87xsee">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-5384" data-mp="22q3h2afl2cq2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-id="element-5385" data-mp="1nt2l1uqczvls">
          <div data-id="element-5386" data-mp="1srdwg9waye64">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5387" data-mp="1squnhiomr048">
              Métricas y Análisis
            </h1>
            <p className="text-gray-500 text-sm mt-1" data-id="element-5388" data-mp="1ez7ovc77tfia">
              Análisis detallado de ventas, tendencias y rendimiento
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportCsv} disabled={analyticsLoading || orders.length === 0} isLoading={isExporting} className="flex-shrink-0" data-id="element-5389" data-mp="15234nhwxju44">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-5390" data-mp="w1wsbzcyx06j" />
            Exportar CSV
          </Button>
        </div>

        {/* ─── Cierre de Caja — La Cabaña Parrillera + NatuCafé + Emilius ─── */}
        {/* Tiene su propio filtro de rango interno (mismos chips Hoy/
            Ayer/Esta Semana/etc. + Desde/Hasta), independiente del
            filtro de abajo para que cada uno cumpla su función. */}
        {showCashClose && <CabanaCashClose restaurantId={restaurant.id} data-id="element-5391" data-mp="2sxrganmo7jec" />}

        {/* Date Filter — debajo de Cierre de Caja. Controla Métricas,
            charts y la tabla de órdenes que siguen abajo. NO afecta
            al Cierre de Caja (ese tiene su propio filtro interno). */}
        <Card data-id="element-5392" data-mp="3gqq20fvegrvl">
          <CardContent className="p-4" data-id="element-5393" data-mp="3gq7wesmul1b0">
            <MetricsDateFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} data-id="element-5394" data-mp="3bq23780xj6qt" />
          </CardContent>
        </Card>

        {/* ─── 🚚 Delivery MS Express — control de pago al repartidor ───
            Sección INDEPENDIENTE: sólo muestra pedidos con delivery_fee > 0
            (no cancelados) y permite marcar si el delivery ya se pagó al
            mensajero. Respeta el filtro de fechas de Métricas. NO afecta
            ventas, totales, cierre de caja, facturación ni KDS. */}
        {showCashClose && <DeliveryMsExpressSection restaurantId={restaurant.id} startDate={startDate} endDate={endDate} data-id="element-5395" data-mp="ks7au6bpeyau" />}

        {/* Loading State */}
        {analyticsLoading ? <div className="space-y-4" data-id="element-5396" data-mp="uitlrn8h70n7">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5397" data-mp="uhqz5ri6rkd3">
              <Skeleton className="h-28 rounded-xl" data-id="element-5398" data-mp="zg29028hduvb" />
              <Skeleton className="h-28 rounded-xl" data-id="element-5399" data-mp="ziy31fh7jzeh" />
              <Skeleton className="h-28 rounded-xl" data-id="element-5400" data-mp="13k05s6ac2tl7" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5401" data-mp="18ibgej8gd84h">
              <Skeleton className="h-36 rounded-xl" data-id="element-5402" data-mp="35ablzo2d4gh2" />
              <Skeleton className="h-36 rounded-xl" data-id="element-5403" data-mp="35bmqjq3mts98" />
              <Skeleton className="h-36 rounded-xl" data-id="element-5404" data-mp="3a8mvtysnqqza" />
              <Skeleton className="h-36 rounded-xl" data-id="element-5405" data-mp="3a9es7bu2wsqm" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5406" data-mp="1g09e7qh9uurv">
              <Skeleton className="h-80 rounded-xl" data-id="element-5407" data-mp="1bzz9eh57uael" />
              <Skeleton className="h-80 rounded-xl" data-id="element-5408" data-mp="1bzh1gn96y7qu" />
            </div>
          </div> : <>
            {/* Peak Insights & KPIs */}
            <MetricsPeakInsights peakDays={analytics.peakDays} peakHours={analytics.peakHours} channelBreakdown={analytics.channelBreakdown} totalOrders={analytics.totalOrders} totalRevenue={analytics.totalRevenue} averageTicket={analytics.averageTicket} data-id="element-5409" data-mp="1krrcg6rwuh95" />

            {/* Charts Row 1: Daily Sales + Hourly Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5410" data-mp="120ru3oex85ou">
              <DailySalesChart data={analytics.dailySales} peakDays={analytics.peakDays} data-id="element-5411" data-mp="2if9uy9bdixqc" />
              <HourlySalesChart data={analytics.hourlySales} peakHours={analytics.peakHours} data-id="element-5412" data-mp="2dg6pwove8rid" />
            </div>

            {/* Charts Row 2: Channel Pie + Top Products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5413" data-mp="45xjgwrh2bmr">
              <ChannelPieChart data={analytics.channelBreakdown} data-id="element-5414" data-mp="3meyf3o6cihh3" />
              <TopProductsChart data={analytics.topProducts} data-id="element-5415" data-mp="3rc81euyfud0p" />
            </div>
          </>}

        {/* Orders history (per restaurant, with date filter, excludes cancelled) */}
        <OrdersHistoryTable orders={orders} orderItems={orderItems} restaurantId={restaurant.id} currencySymbol={restaurant.currency_symbol} data-id="element-5416" data-mp="myi6vyoejoj6" />

        {/* Profitability Table — collapsible. The chevron in the header toggles the rows. */}
        <Card data-id="element-5417" data-mp="e3v8hmh5ocgf">
          <CardHeader data-id="element-5418" data-mp="11w294xtwz5ln">
            <button type="button" onClick={() => setIsProfitabilityOpen(v => !v)} aria-expanded={isProfitabilityOpen} aria-controls="profitability-body" className="w-full flex items-center justify-between gap-3 text-left rounded-lg -m-1 p-1 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-charcoal/40" data-id="element-5419" data-mp="11viubpzk8xbu">
              <CardTitle data-id="element-5420" data-mp="26dooubq5lse7">Rentabilidad por Producto</CardTitle>
              <div className="flex items-center gap-2 text-xs text-gray-500 font-medium pr-1" data-id="element-5421" data-mp="2ac5yp30ylgzb">
                <span className="hidden sm:inline" data-id="element-5422" data-mp="1wkre4rrel7q5">
                  {isProfitabilityOpen ? 'Ocultar' : 'Mostrar'}
                </span>
                {!isProfitabilityOpen && profitability.length > 0 && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold tabular-nums" data-id="element-5423" data-mp="1moxqodc5bttl">
                    {profitability.length}
                  </span>}
                <ChevronDown className={isProfitabilityOpen ? 'w-5 h-5 text-gray-400 transition-transform duration-200 rotate-180' : 'w-5 h-5 text-gray-400 transition-transform duration-200'} data-id="element-5424" data-mp="1dnggfblf6mct" />
              </div>
            </button>
          </CardHeader>
          {isProfitabilityOpen && <CardContent id="profitability-body" data-id="element-5425" data-mp="1828wmr0ny8ul">
              {metricsLoading ? <div className="space-y-4" data-id="element-5426" data-mp="13xrcuruazvjt">
                  <Skeleton className="h-12 w-full" data-id="element-5427" data-mp="1hz3oir2flazw" />
                  <Skeleton className="h-12 w-full" data-id="element-5428" data-mp="1hxiyner2ag8d" />
                  <Skeleton className="h-12 w-full" data-id="element-5429" data-mp="1d0afpby3a862" />
                  <Skeleton className="h-12 w-full" data-id="element-5430" data-mp="1czqvfokyv7ov" />
                  <Skeleton className="h-12 w-full" data-id="element-5431" data-mp="1rpeyovcpkgf8" />
                </div> : profitability.length > 0 ? <div className="overflow-x-auto -mx-6" data-id="element-5432" data-mp="1mr50sczln1px">
                  <table className="w-full text-sm text-left min-w-[600px]" data-id="element-5433" data-mp="20pbqh5ue9vei">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-5434" data-mp="20niedb10iiz4">
                      <tr data-id="element-5435" data-mp="1vsbigpdziikr">
                        <th className="px-6 py-3 rounded-l-lg" data-id="element-5436" data-mp="td2m2r6i0q5t">Producto</th>
                        <th className="px-4 py-3" data-id="element-5437" data-mp="tazkrazwbals">Categoría</th>
                        <th className="px-4 py-3 text-right" data-id="element-5438" data-mp="yd8fabsf2kq0">Precio Venta</th>
                        <th className="px-4 py-3 text-right" data-id="element-5439" data-mp="12d8sxu7oyrw0">Costo Est.</th>
                        <th className="px-4 py-3 text-right" data-id="element-5440" data-mp="174h3wuoo6lba">Ganancia</th>
                        <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-5441" data-mp="175s8gwpxvx3g">
                          Margen
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5442" data-mp="1h13odid33acs">
                      {profitability.map(item => {
                  const isGoodMargin = item.margin_percent >= 60;
                  const isBadMargin = item.margin_percent < 30;
                  return <tr key={item.menu_item_id} className="border-b border-gray-50 last:border-0" data-id="element-5443" data-mp="3ntmjnyl8otf7">
                            <td className="px-6 py-3 font-medium text-gray-900" data-id="element-5444" data-mp="3kaxaxiwimgh1">
                              {item.item_name}
                            </td>
                            <td className="px-4 py-3 text-gray-500" data-id="element-5445" data-mp="3fj5o9jghcuw6">
                              {item.category || '—'}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-5446" data-mp="36klfpb4mw1lo">
                              {formatCurrency(item.price)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500" data-id="element-5447" data-mp="72axh2cyj0yg">
                              {formatCurrency(item.estimated_cost)}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-emerald-600" data-id="element-5448" data-mp="3u7ju83970zgy">
                              {formatCurrency(item.estimated_margin)}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-5449" data-mp="2ivj1kd97kqvx">
                              <div className="flex items-center justify-end gap-2" data-id="element-5450" data-mp="2du3decbqi49b">
                                <span className={`font-bold ${isGoodMargin ? 'text-emerald-600' : isBadMargin ? 'text-red-600' : 'text-amber-600'}`} data-id="element-5451" data-mp="5lwzol9c9jg7">
                                  {Math.round(item.margin_percent)}%
                                </span>
                                {isGoodMargin ? <TrendingUp className="w-4 h-4 text-emerald-500" data-id="element-5452" data-mp="ocwpfoonqxaj" /> : isBadMargin ? <TrendingDown className="w-4 h-4 text-red-500" data-id="element-5453" data-mp="tayaug9k38p6" /> : null}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div> : <div className="text-center py-8 text-gray-500 text-sm" data-id="element-5454" data-mp="351k3vhjpk0zo">
                  No hay datos de rentabilidad disponibles. Asegúrate de
                  configurar los costos en el inventario.
                </div>}
            </CardContent>}
        </Card>
      </div>
    </AdminLayout>;
}